import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, User, ArrowRight, CheckCircle2, Linkedin } from "lucide-react";
import Reveal from "../components/Reveal";
import { company, servicesOverview } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "Insurance", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0d0d10] border border-white/12 px-4 py-3.5 text-stone-100 placeholder-stone-500 text-sm focus:outline-none focus:border-[#c8a951] transition-colors";

  return (
    <div className="pt-[72px]">
      <section className="relative py-24 lg:py-28 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-8">Contact</p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-5xl lg:text-[4.5rem] font-bold tracking-tight leading-[1.02] max-w-4xl">
              Start a <span className="gold-text">conversation.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 text-stone-400 text-lg leading-relaxed max-w-2xl">
              Tell us about your goals. We’ll respond within one business day to schedule a
              confidential consultation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-2xl font-semibold mb-8">Direct contact</h2>
              <div className="space-y-6">
                <ContactRow icon={User} label="Principal" value={company.contact.principal} />
                <ContactRow icon={MapPin} label="Office" value={company.contact.address} />
                <ContactRow icon={Phone} label="Phone" value={company.contact.phone} href={`tel:${company.contact.phone}`} />
                <ContactRow icon={Mail} label="Email" value={company.contact.email} href={`mailto:${company.contact.email}`} />
              </div>

              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/in/leland-nicholson-ains-ais-1784a9367"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 flex items-center gap-4 border border-white/10 clip-corner p-4 hover:border-[#c8a951]/50 hover:bg-[#c8a951]/[0.04] transition-colors group"
              >
                <img
                  src={company.contact.photo}
                  alt={company.contact.principal}
                  style={{ objectPosition: "50% 48%" }}
                  className="w-14 h-14 rounded-full object-cover border border-white/15 shrink-0"
                />
                <div className="min-w-0">
                  <p className="font-mono-l text-[11px] tracking-[0.2em] uppercase text-stone-500">Connect on</p>
                  <span className="flex items-center gap-2 text-stone-200 group-hover:text-[#c8a951] transition-colors mt-0.5">
                    <Linkedin className="w-4 h-4 text-[#c8a951]" /> LinkedIn Profile
                  </span>
                </div>
              </a>
              <div className="mt-10 border border-white/10 clip-corner p-7">
                <p className="eyebrow mb-4">Practice areas</p>
                <ul className="space-y-3">
                  {servicesOverview.map((s) => (
                    <li key={s.id} className="flex items-center gap-3 text-stone-300 text-sm">
                      <span className="h-px w-6 bg-[#c8a951]" /> {s.title}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={120}>
              {submitted ? (
                <div className="border border-[#c8a951]/40 clip-corner p-12 text-center bg-[#c8a951]/5">
                  <CheckCircle2 className="w-14 h-14 text-[#c8a951] mx-auto mb-6" strokeWidth={1.5} />
                  <h3 className="font-display text-2xl font-semibold mb-3">Thank you, {form.name.split(" ")[0] || "friend"}.</h3>
                  <p className="text-stone-400 max-w-md mx-auto">
                    Your inquiry has been received. A member of our team will reach out within one
                    business day.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", interest: "Insurance", message: "" }); }}
                    className="btn-ghost clip-corner mt-8 px-6 py-3 text-sm"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="border border-white/10 clip-corner p-8 lg:p-10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Full name">
                      <input name="name" required value={form.name} onChange={handleChange} placeholder="Jane Doe" className={inputCls} />
                    </Field>
                    <Field label="Email">
                      <input type="email" name="email" required value={form.email} onChange={handleChange} placeholder="jane@email.com" className={inputCls} />
                    </Field>
                    <Field label="Phone">
                      <input name="phone" value={form.phone} onChange={handleChange} placeholder="(864) 000-0000" className={inputCls} />
                    </Field>
                    <Field label="Area of interest">
                      <select name="interest" value={form.interest} onChange={handleChange} className={inputCls}>
                        <option>Insurance</option>
                        <option>Investing</option>
                        <option>Property Management</option>
                        <option>General inquiry</option>
                      </select>
                    </Field>
                  </div>
                  <div className="mt-5">
                    <Field label="How can we help?">
                      <textarea name="message" required rows={5} value={form.message} onChange={handleChange} placeholder="Tell us about your goals..." className={inputCls + " resize-none"} />
                    </Field>
                  </div>
                  {error && (
                    <p className="mt-4 text-sm text-red-400">{error}</p>
                  )}
                  <button type="submit" disabled={loading} className="btn-gold clip-corner mt-7 w-full sm:w-auto px-8 py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? "Submitting..." : "Submit inquiry"} <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 border border-white/12 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#c8a951]" strokeWidth={1.5} />
      </div>
      <div>
        <p className="font-mono-l text-[11px] tracking-[0.2em] uppercase text-stone-500">{label}</p>
        <p className="text-stone-200 mt-1">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:opacity-80 transition-opacity">{content}</a>
  ) : (
    content
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono-l text-[11px] tracking-[0.18em] uppercase text-stone-500 mb-2 block">{label}</span>
      {children}
    </label>
  );
}
